import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { FiLayers, FiLayout, FiMaximize2, FiMoreVertical } from 'react-icons/fi';
import { Tooltip } from '../../../../../components/ds';

const ChartOutputNode = ({ data, isConnectable, selected }) => {
  const isMain = data.panel === 'main';
  // Structural output panel — MAIN vs SUB kept as a colour cue via the harmonized
  // palette (indigo main / fuchsia sub), on a neutral DS box.
  const accent = isMain ? 'var(--node-comparator)' : 'var(--node-logic)';

  const handleMenuClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const nodeElement = e.currentTarget.closest('.react-flow__node');
      if (nodeElement) {
          const event = new MouseEvent('contextmenu', {
              bubbles: true,
              cancelable: true,
              clientX: e.clientX,
              clientY: e.clientY
          });
          nodeElement.dispatchEvent(event);
      }
  };

  return (
    <div
        className={`
        relative min-w-[200px] rounded-xl bg-bg-2 shadow-xl transition-all duration-200 group
        ${selected ? 'ring-2 ring-[var(--color-accent)] border border-transparent' : 'border border-[var(--divider)] hover:border-[var(--border)]'}
        `}
        onMouseEnter={() => data.onHover && data.onHover(true)}
        onMouseLeave={() => data.onHover && data.onHover(false)}
    >
      {/* MAIN/SUB classification stripe — clipped to the node's rounded corners */}
      <div className="absolute inset-0 rounded-xl overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: accent }} />
      </div>

      {/* Target Handle (Input) - RESTORED to allow actual wiring for highlighting */}
      <Handle
        type="target"
        position={Position.Left}
        isConnectable={isConnectable}
        className="!w-2.5 !h-2.5 !border-2 !border-[var(--bg-2)] transition-transform hover:scale-125"
        style={{ background: accent }}
      />

      {/* Header */}
      <div className="px-4 py-3 pl-5 flex items-center justify-between border-b border-[var(--divider)] bg-bg-3">
          <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-lg text-white" style={{ background: accent }}>
                  {isMain ? <FiLayers size={14} /> : <FiLayout size={14} />}
              </div>
              <span className="text-xs font-bold text-fg-1 tracking-wide uppercase">
                  {isMain ? 'Main Chart' : 'Sub Chart'}
              </span>
          </div>

          <Tooltip content="More Options" placement="top">
          <button
              className="text-fg-3 hover:text-fg-1 transition-colors"
              onClick={handleMenuClick}
              aria-label="More Options"
          >
              <FiMoreVertical size={14} />
          </button>
          </Tooltip>
      </div>

      {/* Body */}
      <div className="p-4 pl-5">
          <div className="space-y-3">
              <div>
                  <label className="text-[10px] font-bold text-fg-3 uppercase tracking-wider mb-1 block">
                      Configuration
                  </label>
                  <div className="bg-bg-3 rounded-lg p-2.5 border border-[var(--divider)] flex items-center justify-between group-hover:border-[var(--border)] transition-colors">
                      <span className="text-xs font-medium text-fg-2">
                          {isMain ? 'Price Overlay' : 'Indicator Panel'}
                      </span>
                      <FiMaximize2 className="text-fg-3" size={12} />
                  </div>
              </div>

              {!isMain && (
                   <div className="flex items-center gap-2">
                       <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: accent }}></div>
                       <span className="text-[10px] text-fg-3">New panel will be created</span>
                   </div>
              )}
          </div>
      </div>

      {/* Footer / Status */}
      <div className="px-4 py-2 pl-5 bg-bg-3 border-t border-[var(--divider)] flex items-center justify-between">
          <span className="text-[10px] font-bold text-fg-3">
              OUTPUT
          </span>
          <span className="text-[10px] font-mono" style={{ color: accent }}>
              ID: {data.id || 'AUTO'}
          </span>
      </div>
    </div>
  );
};

export default memo(ChartOutputNode);
